import React from 'react';
import Box from '@mui/material/Box';
import Input, { InputProps } from '@mui/material/Input';

interface MyInputProps extends Omit<InputProps, 'onChange'> {
  label?: React.ReactNode;
  onChange?: (value: string) => void;
  helperText?: string | React.ReactNode;
  inputSx?: React.CSSProperties | Record<string, React.CSSProperties>;
  subLabel?: string;
  handleOnBlur?: () => void;
  handleOnFocus?: () => void;
  maxLength?: number;
  labelColor?: string;
  myInputRef?: React.MutableRefObject<HTMLInputElement | undefined>;
}

const MyInput = (props: MyInputProps) => {
  const {
    value,
    onChange,
    placeholder = '',
    className = '',
    label = '',
    helperText = '',
    labelColor = 'text-black',
    error = false,
    sx = {},
    inputSx = {},
    disabled = false,
    startAdornment = <></>,
    endAdornment = <></>,
    inputProps = {},
    inputRef,
    subLabel,
    handleOnBlur,
    handleOnFocus,
    maxLength = 50,
    myInputRef,
    readOnly,
    ...otherProps
  } = props;

  const handleLocalChange: React.ChangeEventHandler<
    HTMLTextAreaElement | HTMLInputElement
  > = (event) => {
    const value = event.target.value;
    onChange && onChange(maxLength > 0 ? value.slice(0, maxLength) : value);
  };

  return (
    <Box className={`flex flex-col ${className}`} sx={sx}>
      {(label || subLabel) && (
        <div className="mb-2 tablet:mb-3 flex flex-col">
          {label && (
            <label
              className={`font-semibold ${labelColor} leading-5.5 text-sm`}
            >
              {label}
            </label>
          )}
          {subLabel && (
            <label className="text-xs mt-1 text-gray-600">{subLabel}</label>
          )}
        </div>
      )}
      <Input
        value={value || ''}
        className="rounded-lg !text-sm !leading-5.5 h-14 bg-slate-100 px-4 tablet:bg-slate-50 tablet:hover:bg-slate-100 tablet:[&.Mui-disabled]:hover:bg-slate-50"
        onChange={handleLocalChange}
        placeholder={placeholder}
        disabled={disabled}
        startAdornment={startAdornment}
        endAdornment={endAdornment}
        inputProps={{
          ...inputProps,
          maxLength: maxLength > 0 ? maxLength : undefined,
        }}
        ref={inputRef}
        inputRef={myInputRef}
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
        sx={{
          color: error ? '#b91c1c' : '#000',
          cursor: readOnly ? 'pointer' : 'text',
          '& .Mui-readOnly': { cursor: readOnly ? 'pointer' : 'text' },
          '&:before': { borderBottom: 0 },
          '&:hover:not(.Mui-disabled):before': {
            borderBottom: '0px',
          },
          '&:after': {
            borderBottom: error ? '2px solid #b91c1c' : '2px solid #2563eb',
          },
          ...inputSx,
        }}
        readOnly={readOnly}
        {...otherProps}
      />
      {error && helperText && (
        <span className="text-red-700 text-xs mt-2">{helperText}</span>
      )}
    </Box>
  );
};

export default MyInput;
